import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from '../constants';
import { Order } from '../types';
import { extractApiErrorMessage } from '../utils/errorHandler';
import { Attribution } from '../utils/attribution';
import { logout } from './authSlice';

export interface CreateOrderPayload {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  shippingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  items: Array<{
    product: string;
    quantity: number;
  }>;
  paymentMethod: string;
  couponCode?: string;
  notes?: string;
  attribution?: Attribution;
}

interface OrdersState {
  myOrders: Order[];
  selectedOrder: Order | null;
  loading: boolean;
  creating: boolean;
  cancelling: boolean;
  error: string | null;
}

const initialState: OrdersState = {
  myOrders: [],
  selectedOrder: null,
  loading: false,
  creating: false,
  cancelling: false,
  error: null
};

export const createOrder = createAsyncThunk<Order, CreateOrderPayload, { rejectValue: string }>(
  'orders/createOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.ORDERS}`, payload);
      return response.data as Order;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to place order'));
    }
  }
);

export const fetchMyOrders = createAsyncThunk<Order[], void, { rejectValue: string }>(
  'orders/fetchMyOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.ORDERS}/my`);
      const result = response.data as { orders: Order[] };
      return result.orders || [];
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to load orders'));
    }
  }
);

export const fetchOrderById = createAsyncThunk<Order, string, { rejectValue: string }>(
  'orders/fetchOrderById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.ORDERS}/${id}`);
      return response.data as Order;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to load order'));
    }
  }
);

export const cancelOrderById = createAsyncThunk<Order, string, { rejectValue: string }>(
  'orders/cancelOrderById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.ORDERS}/${id}/cancel`);
      return response.data as Order;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to cancel order'));
    }
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearSelectedOrder(state) {
      state.selectedOrder = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.myOrders = action.payload;
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to load orders';
      })
      .addCase(createOrder.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.creating = false;
        state.selectedOrder = action.payload;
        state.myOrders = [action.payload, ...state.myOrders];
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload || action.error.message || 'Failed to place order';
      })
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to load order';
      })
      .addCase(cancelOrderById.pending, (state) => {
        state.cancelling = true;
        state.error = null;
      })
      .addCase(cancelOrderById.fulfilled, (state, action) => {
        state.cancelling = false;
        state.selectedOrder = action.payload;
        state.myOrders = state.myOrders.map((order) =>
          order._id === action.payload._id ? action.payload : order
        );
      })
      .addCase(cancelOrderById.rejected, (state, action) => {
        state.cancelling = false;
        state.error = action.payload || action.error.message || 'Failed to cancel order';
      })
      .addCase(logout, (state) => {
        state.myOrders = [];
        state.selectedOrder = null;
        state.loading = false;
        state.creating = false;
        state.cancelling = false;
        state.error = null;
      });
  }
});

export const { clearSelectedOrder } = ordersSlice.actions;
export default ordersSlice.reducer;
